// @ts-nocheck
import type { ValidationIssue } from '../../diagnostics';
import type { PropertyDefinition } from '../../types/canonical';
import { coerceListValue } from '../variables/listValueUtils';
import type { CoercionPolicy } from './types';

/**
 * Coerces a raw storage string into a typed runtime value for the given property.
 * Returns the typed value and any issues encountered.
 * Never throws.
 */
export function coerceReadValue(
  rawValue: string,
  definition: PropertyDefinition,
  policy: CoercionPolicy,
): { typedValue: unknown; issues: ValidationIssue[] } {
  const issues: ValidationIssue[] = [];

  switch (definition.type) {
    case 'text':
    case 'multiline':
    case 'url':
    case 'email':
    case 'phone':
    case 'color':
    case 'image':
      return { typedValue: rawValue, issues };

    case 'integer': {
      if (!policy.allowStringToNumber) {
        return { typedValue: rawValue, issues };
      }
      const parsed = parseInt(rawValue, 10);
      if (isNaN(parsed)) {
        issues.push({
          severity: 'warning',
          code: 'schema.invalid_field_type',
          message: `Cannot coerce "${rawValue}" to integer for property "${definition.id}".`,
          path: definition.id,
        });
        return { typedValue: undefined, issues };
      }
      return { typedValue: parsed, issues };
    }

    case 'float': {
      if (!policy.allowStringToNumber) {
        return { typedValue: rawValue, issues };
      }
      const parsed = parseFloat(rawValue);
      if (isNaN(parsed)) {
        issues.push({
          severity: 'warning',
          code: 'schema.invalid_field_type',
          message: `Cannot coerce "${rawValue}" to float for property "${definition.id}".`,
          path: definition.id,
        });
        return { typedValue: undefined, issues };
      }
      return { typedValue: parsed, issues };
    }

    case 'boolean': {
      if (!policy.allowStringToBoolean) {
        return { typedValue: rawValue, issues };
      }
      const lower = rawValue.toLowerCase();
      if (lower === 'true' || lower === '1' || lower === 'yes') {
        return { typedValue: true, issues };
      }
      if (lower === 'false' || lower === '0' || lower === 'no') {
        return { typedValue: false, issues };
      }
      issues.push({
        severity: 'warning',
        code: 'schema.invalid_field_type',
        message: `Cannot coerce "${rawValue}" to boolean for property "${definition.id}". Expected true/false/1/0/yes/no.`,
        path: definition.id,
      });
      return { typedValue: undefined, issues };
    }

    case 'date': {
      const datePattern = /^\d{4}-\d{2}-\d{2}$/;
      if (!datePattern.test(rawValue)) {
        issues.push({
          severity: 'warning',
          code: 'schema.invalid_field_type',
          message: `Value "${rawValue}" does not match expected date format YYYY-MM-DD for property "${definition.id}".`,
          path: definition.id,
        });
      }
      return { typedValue: rawValue, issues };
    }

    case 'datetime': {
      // Validate ISO 8601: must have date part, T separator, and time part
      const iso8601Pattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;
      if (!iso8601Pattern.test(rawValue)) {
        issues.push({
          severity: 'warning',
          code: 'schema.invalid_field_type',
          message: `Value "${rawValue}" does not match expected ISO 8601 datetime format for property "${definition.id}".`,
          path: definition.id,
        });
      }
      return { typedValue: rawValue, issues };
    }

    case 'enum': {
      const enumValues = definition.validation?.enumValues ?? [];
      const match = enumValues.find((ev: { id: string }) => ev.id === rawValue);
      if (match) {
        return { typedValue: match.id, issues };
      }
      if (policy.strictEnumValues) {
        issues.push({
          severity: 'error',
          code: 'schema.invalid_field_type',
          message: `Value "${rawValue}" is not a valid enum option for property "${definition.id}".`,
          path: definition.id,
        });
        return { typedValue: undefined, issues };
      }
      issues.push({
        severity: 'warning',
        code: 'schema.invalid_field_type',
        message: `Value "${rawValue}" is not a recognized enum option for property "${definition.id}".`,
        path: definition.id,
      });
      return { typedValue: rawValue, issues };
    }

    case 'json': {
      try {
        const parsed = JSON.parse(rawValue);
        return { typedValue: parsed, issues };
      } catch {
        issues.push({
          severity: 'error',
          code: 'schema.invalid_field_type',
          message: `Cannot parse JSON value for property "${definition.id}": ${rawValue}`,
          path: definition.id,
        });
        return { typedValue: undefined, issues };
      }
    }

    case 'list': {
      try {
        const parsed = JSON.parse(rawValue) as unknown;
        const coerced = coerceListValue(parsed);
        if (coerced === null) {
          issues.push({
            severity: 'error',
            code: 'schema.invalid_field_type',
            message: `Value for property "${definition.id}" is not a valid list (array, max elements, JSON-serializable).`,
            path: definition.id,
          });
          return { typedValue: undefined, issues };
        }
        return { typedValue: coerced, issues };
      } catch {
        issues.push({
          severity: 'error',
          code: 'schema.invalid_field_type',
          message: `Cannot parse list JSON for property "${definition.id}": ${rawValue}`,
          path: definition.id,
        });
        return { typedValue: undefined, issues };
      }
    }

    case 'table':
    case 'fileTable': {
      // table: CTBL base64 blob. fileTable: FileTableManifest JSON string.
      return { typedValue: rawValue, issues };
    }

    default: {
      // Exhaustive fallback — return as-is
      return { typedValue: rawValue, issues };
    }
  }
}
